import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TurnstileWidget from '../TurnstileWidget';
import { publicApi } from '../../api/insolita';
import { BookingResult, DayAvailability, SlotAvailability, Tour } from '../../api/types';
import { euro } from '../../utils/text';
import { useUi } from '../../i18n/ui';
import { resolveTour } from '../../i18n/tour';
import AvailabilityCalendar, { DateRange } from './AvailabilityCalendar';
import SlotPickerCalendar from './SlotPickerCalendar';

export interface BookingPreselect {
  tourId: number;
  slotId?: number;
  checkIn?: string;
  /** Cambia a ogni click nel calendario per riattivare la preselezione anche sulla stessa data. */
  nonce: number;
}

interface Props {
  tours: Tour[];
  slots: SlotAvailability[];
  preselect?: BookingPreselect;
}

const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const daysInclusive = (a: string, b: string) => Math.round((parseISO(b).getTime() - parseISO(a).getTime()) / 86400000) + 1;

function slotDays(slot: SlotAvailability, tour?: Tour): number {
  if (slot.startDate && slot.endDate) {
    const d = Math.round((new Date(slot.endDate).getTime() - new Date(slot.startDate).getTime()) / 86400000) + 1;
    if (d > 0) return d;
  }
  return tour?.durationDays || 1;
}

const BookingForm: React.FC<Props> = ({ tours, slots, preselect }) => {
  const { tr, lang, dateLocale } = useUi();
  const [tourId, setTourId] = useState<number | ''>('');
  const [slotId, setSlotId] = useState<number | ''>('');
  const [range, setRange] = useState<DateRange>({});
  const [calendar, setCalendar] = useState<DayAvailability[]>([]);
  const [calLoading, setCalLoading] = useState(false);
  const [people, setPeople] = useState(3);
  const [level, setLevel] = useState('1');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BookingResult | null>(null);
  // Antispam
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const handleCaptcha = useCallback((token: string | null) => setCaptchaToken(token), []);
  const [website, setWebsite] = useState(''); // honeypot

  const fmtDate = (d?: string): string => (d ? new Date(d).toLocaleDateString(dateLocale, { day: '2-digit', month: 'short', year: 'numeric' }) : '');

  const tour = useMemo(() => tours.find((t) => t.id === tourId), [tours, tourId]);
  const isFlexible = tour?.bookingMode === 'FLEXIBLE';

  const tourSlots = useMemo(
    () => slots.filter((s) => s.tourId === tourId && s.status === 'OPEN'),
    [slots, tourId]
  );
  const slot = useMemo(() => slots.find((s) => s.id === slotId), [slots, slotId]);

  useEffect(() => {
    if (!tour || tour.bookingMode !== 'FLEXIBLE') { setCalendar([]); return; }
    const from = new Date();
    const to = new Date(); to.setMonth(to.getMonth() + 6);
    setCalLoading(true);
    publicApi.tourCalendar(tour.id, isoOf(from), isoOf(to))
      .then(setCalendar)
      .catch(() => setCalendar([]))
      .finally(() => setCalLoading(false));
  }, [tour]);

  // Preselezione dal calendario vetrina: imposta tour + slot/periodo.
  useEffect(() => {
    if (!preselect) return;
    setTourId(preselect.tourId);
    setSlotId(preselect.slotId ?? '');
    setRange(preselect.checkIn ? { checkIn: preselect.checkIn, checkOut: undefined } : {});
    setError(null);
  }, [preselect]);

  const changeTour = (id: number) => {
    setTourId(id);
    setSlotId('');
    setRange({});
    setError(null);
  };

  const preventivo = useMemo(() => {
    if (isFlexible) {
      if (!tour || !range.checkIn || !range.checkOut) return null;
      const days = daysInclusive(range.checkIn, range.checkOut);
      const rate = tour.pricePerPersonPerDay || 0;
      const total = rate * people * days;
      const deposit = total * ((tour.depositPercent ?? 25) / 100);
      const byDate = new Map(calendar.map((d) => [d.date, d]));
      let waitlist = false;
      for (let i = 0; i < days; i++) {
        const dISO = isoOf(new Date(parseISO(range.checkIn).getTime() + i * 86400000));
        const d = byDate.get(dISO);
        if (!d || d.availableSeats < people) { waitlist = true; break; }
      }
      return { days, total, deposit, waitlist };
    }
    if (!slot) return null;
    const days = slotDays(slot, tour);
    const rate = slot.pricePerPersonPerDay || tour?.pricePerPersonPerDay || 0;
    const total = rate * people * days;
    const deposit = total * ((tour?.depositPercent ?? 25) / 100);
    return { days, total, deposit, waitlist: slot.availableSeats < people };
  }, [isFlexible, slot, tour, people, range, calendar]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (isFlexible) {
      if (!range.checkIn || !range.checkOut) { setError(tr('bf.err.period')); return; }
    } else if (!slotId) {
      setError(tr('bf.err.date')); return;
    }
    if (!consent) { setError(tr('bf.err.consent')); return; }
    if (!captchaToken) { setError(tr('bf.err.captcha') || 'Completa la verifica anti-bot.'); return; }
    setSubmitting(true);
    try {
      const res = await publicApi.createBooking({
        ...(isFlexible
          ? { tourId: tour!.id, checkInDate: range.checkIn, checkOutDate: range.checkOut }
          : { slotId: slotId as number }),
        customerName: `${name} ${surname}`.trim(),
        customerEmail: email,
        customerPhone: phone,
        people,
        level: tr(`bf.level.${level}`),
        notes,
        consent,
        captchaToken,
        website,
      });
      setResult(res);
      setCaptchaToken(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || tr('bf.err.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="book-form">
        <h3 className="h-card" style={{ color: 'var(--acid)', marginBottom: '.5em' }}>{tr('bf.result.title')}</h3>
        <p style={{ color: 'rgba(244,241,233,.8)' }}>{result.message}</p>
        {result.waitlistLikely && (
          <p style={{ color: 'var(--acid)', marginTop: 14 }}>{tr('bf.result.waitlist')}</p>
        )}
      </div>
    );
  }

  return (
    <form className="book-form" onSubmit={submit}>
      <div className="form-grid">
        <div className="field full">
          <label htmlFor="b-tour">{tr('bf.tour')}</label>
          <select id="b-tour" value={tourId} onChange={(e) => changeTour(Number(e.target.value))}>
            <option value="">{tr('bf.tour.ph')}</option>
            {tours.map((tt) => {
              const r = resolveTour(tt, lang);
              return <option key={tt.id} value={tt.id}>{r.title}{r.subtitle ? ` — ${r.subtitle}` : ''}</option>;
            })}
          </select>
        </div>

        {isFlexible ? (
          <div className="field full">
            <label>{tr('bf.period')}</label>
            {range.checkIn && (
              <p className="book-hint" style={{ marginBottom: 10, color: 'var(--acid)' }}>
                {range.checkOut
                  ? `${fmtDate(range.checkIn)} → ${fmtDate(range.checkOut)} · ${daysInclusive(range.checkIn, range.checkOut)} ${tr('bf.days')}`
                  : `${fmtDate(range.checkIn)} → ${tr('bf.period.selectEnd')}`}
              </p>
            )}
            <AvailabilityCalendar days={calendar} people={people} value={range} onChange={setRange} loading={calLoading} />
          </div>
        ) : (
          <div className="field full">
            <label>{tr('bf.date')}</label>
            {!tourId ? (
              <p className="book-hint">{tr('bf.date.ph.first')}</p>
            ) : tourSlots.length === 0 ? (
              <p className="book-hint">{tr('bf.date.ph.none')}</p>
            ) : (
              <>
                {slot && (
                  <p className="book-hint" style={{ marginBottom: 10, color: 'var(--acid)' }}>
                    {fmtDate(slot.startDate)}{slot.endDate ? ` → ${fmtDate(slot.endDate)}` : ''} · {slot.availableSeats > 0 ? `${slot.availableSeats} ${tr('bf.seatsFree')}` : tr('bf.waitlist')}
                  </p>
                )}
                <SlotPickerCalendar
                  slots={tourSlots}
                  value={typeof slotId === 'number' ? slotId : undefined}
                  onChange={(id) => setSlotId(id)}
                />
              </>
            )}
          </div>
        )}

        <div className="field">
          <label htmlFor="b-persone">{tr('bf.people')}</label>
          <input id="b-persone" type="number" min={tour?.minPeople || 1}
            max={tour && tour.maxPeoplePerBooking > 0 ? tour.maxPeoplePerBooking : undefined} value={people}
            onChange={(e) => setPeople(Math.max(1, parseInt(e.target.value || '1', 10)))} />
          {tour && (
            <span className="book-hint">
              {tr('bf.people.min')} {tour.minPeople}{tour.maxPeoplePerBooking > 0 ? ` · ${tr('bf.people.max')} ${tour.maxPeoplePerBooking}` : ''} {tr('bf.people.part')}
            </span>
          )}
        </div>
        <div className="field">
          <label htmlFor="b-livello">{tr('bf.level')}</label>
          <select id="b-livello" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="1">{tr('bf.level.1')}</option><option value="2">{tr('bf.level.2')}</option><option value="3">{tr('bf.level.3')}</option>
          </select>
        </div>
      </div>

      {preventivo && (
        <div className="book-result">
          <div className="line"><span>{tr('bf.prev.duration')}</span><b>{preventivo.days} {tr('bf.days')} · {people} {tr('bf.prev.people')}</b></div>
          <div className="line total"><span>{tr('bf.prev.total')}</span><b>{euro(preventivo.total)}</b></div>
          <div className="line"><span>{tr('bf.prev.deposit')} ({tour?.depositPercent ?? 25}%)</span><b>{euro(preventivo.deposit)}</b></div>
          {preventivo.waitlist && (
            <p className="book-hint" style={{ marginTop: 12, color: '#E4A11A' }}>{tr('bf.prev.waitlistNote')}</p>
          )}
          <p className="book-hint" style={{ marginTop: 12 }}>{tr('bf.prev.estimate')}</p>
        </div>
      )}

      <div className="form-grid" style={{ marginTop: 26 }}>
        <div className="field"><label htmlFor="b-nome">{tr('bf.name')}</label><input id="b-nome" value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div className="field"><label htmlFor="b-cognome">{tr('bf.surname')}</label><input id="b-cognome" value={surname} onChange={(e) => setSurname(e.target.value)} required /></div>
        <div className="field"><label htmlFor="b-email">{tr('bf.email')}</label><input id="b-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
        <div className="field"><label htmlFor="b-tel">{tr('bf.phone')}</label><input id="b-tel" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required /></div>
        <div className="field full"><label htmlFor="b-note">{tr('bf.notes')}</label><textarea id="b-note" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={tr('bf.notes.ph')} /></div>
      </div>

      <label className="consent">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required /> {tr('bf.consent')}
      </label>

      {/* Honeypot antispam: nascosto agli utenti umani, compilato dai bot generici. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
        <label>Non compilare questo campo
          <input type="text" tabIndex={-1} autoComplete="off" name="website" value={website} onChange={(e) => setWebsite(e.target.value)} />
        </label>
      </div>
      <TurnstileWidget onToken={handleCaptcha} theme="dark" />

      {error && <p style={{ color: '#ff5a5a', marginTop: 12, fontSize: '.9rem' }}>{error}</p>}

      <button type="submit" className="btn btn-acid" style={{ marginTop: 24 }} disabled={submitting || !captchaToken}>
        {submitting ? tr('bf.submitting') : tr('bf.submit')}
      </button>
    </form>
  );
};

export default BookingForm;
